import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    ReactNode,
} from "react";
import {
    User as FirebaseUser,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    updateProfile,
} from "firebase/auth";
import { auth } from "../firebase.config";
import { APP_CONFIG } from "../config/app.config";
import {
    userApi,
    User as DBUser,
    CreateUserData,
    profileApi,
    Profile,
} from "../services/api";

interface AuthContextType {
    // Firebase user
    firebaseUser: FirebaseUser | null;
    // Database user
    dbUser: DBUser | null;
    // User profiles
    profiles: Profile[];
    // Loading states
    loading: boolean;
    profilesLoading: boolean;
    // Auth functions
    signUp: (
        email: string,
        password: string,
        firstName: string,
        lastName: string
    ) => Promise<void>;
    signIn: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    resendVerificationEmail: (email?: string, password?: string) => Promise<void>;
    refreshAuthState: () => Promise<void>;
    // User functions
    updateUserProfile: (userData: Partial<CreateUserData>) => Promise<void>;
    refreshUser: () => Promise<void>;
    updateUserApiKey: (apiKey: string) => void;
    // Profile functions
    refreshProfiles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [dbUser, setDbUser] = useState<DBUser | null>(null);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [profilesLoading, setProfilesLoading] = useState(true);
    const pendingSignupData = useRef<{
        password?: string;
        firstName?: string;
        lastName?: string;
    } | null>(null);

    // Create or get database user from Firebase user
    const syncDatabaseUser = async (firebaseUser: FirebaseUser) => {
        try {
            // First try to get existing user
            let dbUserData: DBUser;

            try {
                dbUserData = await userApi.getUserByFirebaseUid(
                    firebaseUser.uid
                );
            } catch (error) {
                // User doesn't exist in database, create them
                let firstName: string;
                let lastName: string;
                
                // Prioritize pending signup data if available
                if (pendingSignupData.current?.firstName && pendingSignupData.current?.lastName !== undefined) {
                    firstName = pendingSignupData.current.firstName;
                    lastName = pendingSignupData.current.lastName;
                    
                } else if (firebaseUser.displayName) {
                    // Parse display name if available
                    const names = firebaseUser.displayName.split(" ");
                    firstName = names[0] || "User";
                    lastName = names.slice(1).join(" ") || "";
                    
                } else {
                    // Fallback values - this should not happen for new signups
                    firstName = "User";
                    lastName = "";
                    console.warn("No name data available, using fallback:", { firstName, lastName });
                }
                
                const createData: CreateUserData = {
                    email: firebaseUser.email!,
                    first_name: firstName,
                    last_name: lastName,
                    firebase_uid: firebaseUser.uid,
                };

                // Add password if this is a signup
                if (pendingSignupData.current?.password) {
                    createData.password = pendingSignupData.current.password;
                }

                dbUserData = await userApi.createUser(createData);
                
                // Clear pending signup data after successful creation
                pendingSignupData.current = null;
            }

            // Update last login
            await userApi.updateLastLogin(dbUserData.id);

            // Get updated user data
            const updatedUser = await userApi.getUserById(dbUserData.id);
            setDbUser(updatedUser);
        } catch (error) {
            // Clear pending signup data on error
            pendingSignupData.current = null;
        }
    };

    // Sign up with email and password
    const signUp = async (
        email: string,
        password: string,
        firstName: string,
        lastName: string
    ) => {
        try {
            // Store signup data for syncDatabaseUser
            pendingSignupData.current = { password, firstName, lastName };
            
            
            // Create Firebase user
            const { user } = await createUserWithEmailAndPassword(
                auth,
                email,
                password
            );

            // Update Firebase profile and wait for it to complete
            const displayName = `${firstName} ${lastName}`.trim();
            
            
            await updateProfile(user, {
                displayName: displayName,
            });

            // Reload the user to ensure we have the updated profile
            await user.reload();
            
            // Note: Email verification is now handled by the backend via SendGrid
            // The backend will send the verification email when the user is created

            // Don't create database user here - let onAuthStateChanged handle it
            // This prevents double creation
        } catch (error) {
            console.error("Error signing up:", error);
            // Clear pending signup data on error
            pendingSignupData.current = null;
            throw error;
        }
    };

    // Sign in with email and password
    const signIn = async (email: string, password: string) => {
        try {
            const { user } = await signInWithEmailAndPassword(auth, email, password);
            
            if (APP_CONFIG.REQUIRE_EMAIL_VERIFICATION && !user.emailVerified) {
                // Reload to get fresh status
                await user.reload();
                
                // Check again after reload
                if (!user.emailVerified) {
                    // Try to get the database user to check if they're verified there
                    try {
                        const dbUser = await userApi.getUserByEmail(email);
                        if (dbUser.is_verified && dbUser.id) {
                            // Database says verified but Firebase doesn't - try to sync
                            console.log("Database verified but Firebase not - attempting sync");
                            await userApi.syncVerificationStatus(dbUser.id);
                            
                            // Reload Firebase user again
                            await user.reload();
                            
                            // Check one more time
                            if (!user.emailVerified) {
                                // Still not verified in Firebase after sync attempt
                                await signOut(auth);
                                throw new Error("Email verification sync failed. Please contact support.");
                            }
                            // If we get here, sync worked and user is now verified
                        } else {
                            // Not verified in database either
                            await signOut(auth);
                            throw new Error("Please verify your email before signing in. Check your inbox for a verification email.");
                        }
                    } catch (syncError) {
                        console.error("Error checking/syncing verification status:", syncError);
                        // Sign out and show verification error
                        await signOut(auth);
                        throw new Error("Please verify your email before signing in. Check your inbox for a verification email.");
                    }
                }
            }
            
            // Firebase auth state change will handle the rest
        } catch (error) {
            console.error("Error signing in:", error);
            throw error;
        }
    };

    // Sign in with Google
    const signInWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const { user } = await signInWithPopup(auth, provider);
            
            // Check email verification if required
            if (APP_CONFIG.REQUIRE_EMAIL_VERIFICATION && !user.emailVerified) {
                // For Google sign-in, the email is typically verified by Google
                // But if you want to enforce your own verification:
                // await signOut(auth);
                // throw new Error("Please verify your email before signing in.");
                
                // Note: Google accounts are usually pre-verified, so we might want to skip this check
                // for Google sign-ins. Uncomment the above lines if you want to enforce it.
            }
            
            // Firebase auth state change will handle the rest
        } catch (error) {
            console.error("Error signing in with Google:", error);
            throw error;
        }
    };

    // Logout
    const logout = async () => {
        try {
            await signOut(auth);
            setDbUser(null);
        } catch (error) {
            console.error("Error logging out:", error);
            throw error;
        }
    };

    // Resend verification email
    const resendVerificationEmail = async (email?: string, password?: string) => {
        try {
            // Use the backend API to resend verification email
            if (email) {
                await userApi.resendVerificationEmail(email);
            } else if (firebaseUser?.email) {
                await userApi.resendVerificationEmail(firebaseUser.email);
            } else {
                throw new Error("No email address available");
            }
        } catch (error) {
            console.error("Error resending verification email:", error);
            throw error;
        }
    };

    // Refresh auth state
    const refreshAuthState = async () => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            // Force reload user data from Firebase
            await currentUser.reload();
            // The onAuthStateChanged listener will pick up the changes
        }
    };

    // Update user profile
    const updateUserProfile = async (userData: Partial<CreateUserData>) => {
        if (!dbUser) throw new Error("No user logged in");

        try {
            const updatedUser = await userApi.updateUser(dbUser.id, userData);
            setDbUser(updatedUser);
        } catch (error) {
            console.error("Error updating profile:", error);
            throw error;
        }
    };

    // Refresh user data
    const refreshUser = async () => {
        if (!dbUser?.id) return;

        try {
            const updatedUser = await userApi.getUserById(dbUser.id);
            setDbUser(updatedUser);
        } catch (error) {
            console.error("Error refreshing user:", error);
        }
    };

    // Update user's API key directly (used after subscription creation)
    const updateUserApiKey = (apiKey: string) => {
        if (!dbUser) return;
        
        setDbUser({
            ...dbUser,
            passioapikey: apiKey
        });
    };

    // Load user profiles
    const refreshProfiles = async () => {
        if (!dbUser?.id) return;

        try {
            setProfilesLoading(true);
            // For AuthContext, we'll load all profiles (up to 100)
            const response = await profileApi.getUserProfiles(dbUser.id, {
                page: 1,
                page_size: 100, // Most users won't have more than 100 profiles
            });
            setProfiles(response.items);
        } catch (error) {
            console.error("Error loading profiles:", error);
            setProfiles([]);
        } finally {
            setProfilesLoading(false);
        }
    };

    // Load profiles when user is authenticated
    useEffect(() => {
        if (dbUser?.id) {
            refreshProfiles();
        } else {
            setProfiles([]);
            setProfilesLoading(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbUser?.id]);

    // Listen to Firebase auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setFirebaseUser(user);

            if (user) {
                // If this is a new signup and we have pending data, wait a bit
                // to ensure the state is properly set
                if (pendingSignupData.current) {
                    
                    // Small delay to ensure state is set
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
                await syncDatabaseUser(user);
            } else {
                setDbUser(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []); // Removed pendingSignupData as dependency

    const value: AuthContextType = {
        firebaseUser,
        dbUser,
        profiles,
        loading,
        profilesLoading,
        signUp,
        signIn,
        signInWithGoogle,
        logout,
        resendVerificationEmail,
        refreshAuthState,
        updateUserProfile,
        refreshUser,
        updateUserApiKey,
        refreshProfiles,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};
